import React, { ChangeEvent, MouseEvent } from 'react';
import { formatPhone, validateEmail, formatMonth, monthOptions, dayOptions, listYears, scrollToTop } from 'utils/utils';
import Line from 'components/Line';
import FormInput from 'components/FormInput';
import FormSelect from 'components/FormSelect';
import FormTextArea from 'components/FormTextArea';
import FormFile from 'components/FormFile';
import NextSteps from 'components/NextSteps';
import SalesCalloutHTO from 'components/SalesCalloutHTO';
import SalesCalloutSwatches from 'components/SalesCalloutSwatches';
import Loading from 'components/Loading';
import FormCheckItem from '../../src/components/FormCheckItem';
import { withPageStaticProps } from 'utils/header';

const removeAllButLast = (input: string, token: string) => {
  const parts = input.split(token);
  return parts.slice(0, -1).join('') + token + parts.slice(-1);
};

const YEARS = listYears(2016, new Date().getFullYear() + 2);

interface State {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  subject: string;
  eventId: string;
  description: string;
  images: File[];
  firstNameErr: boolean;
  lastNameErr: boolean;
  emailErr: boolean;
  invalidEmailErr: boolean;
  phoneErr: boolean;
  subjectErr: boolean;
  descriptionErr: boolean;
  success: boolean;
  submitting: boolean;
  error: boolean;
  eventYear: number;
  eventMonth: number;
  eventDay: number;
  isTraveling: boolean;
  travelDateYear: number;
  travelDateMonth: number;
  travelDateDay: number;
}

/**
 * Maps a subject to its tag in Zendesk, which is similar to the `name` of an `<option>`
 * element. This tag is used to specify which value we want to use for the `Reason` field
 * when creating a support ticket.
 */
const SUBJECT_TO_TAG = {
  'Request New Size/Items': 'request_new_size/items',
  'Event Management': 'event_management',
  'Fabric Swatch Program': 'swatches',
  'Home Try-On Program': 'home_try_on',
  'Shipping/Order Inquiry': 'shipping/order_inquiry',
  'Rental Return': 'rental_return',
  'Buy My Rental': 'buy_my_rental',
  'Promotional Codes': 'promotional_codes',
  'Retail Purchase/Inquiry': 'retail_purchase/inquiry',
  'Showroom/Virtual Consultation': 'showroom/virtual_consultation',
  'Bridal Show Follow-Up': 'bridal_show_follow-up',
  'Event Postponed/Cancelled': 'event_postponed_cancelled',
  'Styled Shoot': 'styled_shoot',
};

type EmailSubject = keyof typeof SUBJECT_TO_TAG;

const validateSubject = (subject: string): subject is EmailSubject => SUBJECT_TO_TAG.hasOwnProperty(subject);

class EmailUs extends React.Component<{}, State> {
  constructor(props: {}) {
    super(props);
    this.state = {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      subject: '',
      eventId: '',
      description: '',
      images: [],
      firstNameErr: false,
      lastNameErr: false,
      emailErr: false,
      invalidEmailErr: false,
      phoneErr: false,
      subjectErr: false,
      descriptionErr: false,
      success: false,
      submitting: false,
      error: false,
      eventYear: new Date().getFullYear(),
      eventMonth: new Date().getMonth() + 1,
      eventDay: new Date().getDate(),
      isTraveling: false,
      travelDateYear: new Date().getFullYear(),
      travelDateMonth: new Date().getMonth() + 1,
      travelDateDay: new Date().getDate(),
    };
  }

  handleChange = (field: keyof State, value: State[keyof State]) =>
    this.setState({ [field]: value } as Pick<State, keyof State>);

  handleImageChange = (files: FileList) =>
    this.setState({
      images: Array.from(files),
    });

  handlePhoneChange = (value: string) => this.setState({ phone: formatPhone(value) });

  validateInput = () => {
    const firstNameErr = this.state.firstName === '';
    const lastNameErr = this.state.lastName === '';
    const emailErr = this.state.email === '';
    const invalidEmailErr = !validateEmail(this.state.email);
    const phoneErr = this.state.phone === '' || this.state.phone.length !== 14;
    const subjectErr = !validateSubject(this.state.subject);
    const descriptionErr = this.state.description === '';

    this.setState((_) => ({
      firstNameErr,
      lastNameErr,
      emailErr,
      invalidEmailErr,
      phoneErr,
      subjectErr,
      descriptionErr,
    }));

    return !firstNameErr && !lastNameErr && !emailErr && !phoneErr && !subjectErr && !descriptionErr;
  };

  pad = (n: number) => (n < 10 ? '0' + n : n);

  submit = async (e: MouseEvent) => {
    e.preventDefault();

    if (!this.validateInput()) {
      return scrollToTop();
    }

    this.setState({ submitting: true, error: false }, () => setTimeout(scrollToTop, 250));

    let formData = new FormData();

    formData.append('firstName', this.state.firstName);
    formData.append('lastName', this.state.lastName);
    formData.append('email', this.state.email);
    formData.append('phone', this.state.phone);
    formData.append(
      'subject',
      `${this.state.subject} - ${this.state.eventMonth}-${this.state.eventDay}-${this.state.eventYear}`
    );
    formData.append('description', this.state.description);
    formData.append('priority', 'none');
    formData.append('reason', SUBJECT_TO_TAG[this.state.subject as keyof typeof SUBJECT_TO_TAG]);

    this.state.images.forEach((_, i) => {
      formData.append(
        'images[]',
        this.state.images[i],
        removeAllButLast(this.state.images[i].name.replace(/\s+/g, ''), '.')
      );
    });

    if (this.state.isTraveling) {
      formData.append(
        'travelDate',
        this.state.travelDateYear +
          '-' +
          this.pad(this.state.travelDateMonth) +
          '-' +
          this.pad(this.state.travelDateDay)
      );
    }

    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_ACCOUNTS_URL}/v3/cases`, {
        method: 'POST',
        body: formData,
      });

      if (response.status >= 400) {
        throw new Error('Non-200 status code received from API');
      }

      this.setState({ success: true });
    } catch (e) {
      console.error(e);
      this.setState({ error: true });
    } finally {
      this.setState({ submitting: false });
    }
  };

  render() {
    if (this.state.submitting) {
      return (
        <div className="container flex justify-center py-64">
          <Loading />
        </div>
      );
    }

    if (this.state.success) {
      return (
        <div className="m-section container flex justify-center">
          <div className="max-w-lg">
            <h1 className="text-h1-display">Thank You</h1>
            <Line />
            <p>
              We are receiving a higher than average amount of customer service requests. We will respond as soon as
              possible. Thanks for your patience.
            </p>
          </div>
        </div>
      );
    }

    return (
      <>
        <div className="container">
          <div className="row">
            <div className="col-span-12">
              <div className="m-section-hero mx-auto max-w-sm">
                <h1 className="text-h1-display mb-8">Email Us</h1>
                <p>
                  <strong>*</strong>required fields
                </p>
                <Line />
                <form>
                  <div className="flex flex-col space-y-32">
                    <FormInput
                      combineLabel
                      label="First Name*"
                      placeholder=""
                      id="firstName"
                      name="firstName"
                      type="text"
                      value={this.state.firstName}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => this.handleChange('firstName', e.target.value)}
                      errorMessage={this.state.firstNameErr ? 'This field is required.' : ''}
                    />

                    <FormInput
                      combineLabel
                      label="Last Name*"
                      placeholder=""
                      id="lastName"
                      name="lastName"
                      type="text"
                      value={this.state.lastName}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => this.handleChange('lastName', e.target.value)}
                      errorMessage={this.state.lastNameErr ? 'This field is required.' : ''}
                    />

                    <FormInput
                      combineLabel
                      label="Email*"
                      placeholder=""
                      id="email"
                      name="email"
                      type="email"
                      value={this.state.email}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => this.handleChange('email', e.target.value)}
                      errorMessage={
                        this.state.emailErr
                          ? 'This field is required.'
                          : this.state.invalidEmailErr
                          ? 'The input is not a valid email address.'
                          : ''
                      }
                    />

                    <FormInput
                      combineLabel
                      label="Mobile Phone*"
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder=""
                      value={this.state.phone}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => this.handlePhoneChange(e.target.value)}
                      errorMessage={this.state.phoneErr ? 'This field is required.' : ''}
                    />

                    <FormSelect
                      combineLabel
                      label="Subject*"
                      id="subject"
                      name="subject"
                      value={this.state.subject}
                      onChange={(e: ChangeEvent<HTMLSelectElement>) => this.handleChange('subject', e.target.value)}
                      errorMessage={this.state.subjectErr ? 'This field is required.' : ''}
                    >
                      <option>Please Select One</option>
                      {Object.keys(SUBJECT_TO_TAG).map((subject) => (
                        <option key={subject} value={subject}>
                          {subject}
                        </option>
                      ))}
                    </FormSelect>

                    {/* Event Date Picker */}

                    <div>
                      <h2 className="text-h4 mb-8">Event Date:</h2>
                      <fieldset className="m-0 flex flex-col space-y-8 p-0 xs:flex-row xs:space-y-0 xs:space-x-8">
                        <legend className="sr-only">Event Field</legend>
                        <FormSelect
                          combineLabel
                          label="Month*"
                          id="month"
                          name="month"
                          className="flex-grow"
                          value={this.state.eventMonth}
                          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                            if (!!e.target.value)
                              this.setState({
                                eventMonth: Number(e.target.value),
                              });
                          }}
                        >
                          {monthOptions.map((month: number) => (
                            <option key={`month-${month}`} value={month}>
                              {formatMonth(month, this.state.eventYear)}
                            </option>
                          ))}
                        </FormSelect>
                        <FormSelect
                          combineLabel
                          label="Day*"
                          id="day"
                          name="day"
                          className="flex-grow"
                          value={this.state.eventDay}
                          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                            if (!!e.target.value)
                              this.setState({
                                eventDay: Number(e.target.value),
                              });
                          }}
                        >
                          {dayOptions(this.state.eventYear, this.state.eventMonth).map((day) => (
                            <option key={`day-${day}`} value={day}>
                              {day}
                            </option>
                          ))}
                        </FormSelect>
                        <FormSelect
                          combineLabel
                          label="Year*"
                          id="year"
                          name="year"
                          className="flex-grow"
                          value={this.state.eventYear}
                          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                            if (!!e.target.value)
                              this.setState({
                                eventYear: Number(e.target.value),
                              });
                          }}
                        >
                          {YEARS.map((year) => (
                            <option key={`year-${year}`} value={year}>
                              {year}
                            </option>
                          ))}
                        </FormSelect>
                      </fieldset>
                    </div>
                    <FormTextArea
                      combineLabel
                      label="Description*"
                      placeholder=""
                      id="description"
                      name="description"
                      value={this.state.description}
                      onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                        this.handleChange('description', e.target.value)
                      }
                      errorMessage={this.state.descriptionErr ? 'This field is required.' : ''}
                    />

                    {/* Travel Date */}
                    {SUBJECT_TO_TAG[this.state.subject as keyof typeof SUBJECT_TO_TAG] === 'request_new_size/items' ? (
                      <>
                        <div className="text-h4">
                          <FormCheckItem
                            label="Will you be traveling out of town for your event?"
                            checked={this.state.isTraveling}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                              this.handleChange('isTraveling', e.target.checked)
                            }
                          />
                        </div>
                        <div>
                          <h2 className="text-h4 mb-8">What is the last date you'll be able to receive a shipment?</h2>
                          <fieldset className="m-0 flex flex-col space-y-8 p-0 xs:flex-row xs:space-y-0 xs:space-x-8">
                            <FormSelect
                              combineLabel
                              label="Month*"
                              id="travelDateMonth"
                              name="travelDateMonth"
                              className="flex-grow"
                              value={this.state.travelDateMonth}
                              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                if (!!e.target.value)
                                  this.setState({
                                    travelDateMonth: Number(e.target.value),
                                  });
                              }}
                            >
                              {monthOptions.map((month: number) => (
                                <option key={`travel-month-${month}`} value={month}>
                                  {formatMonth(month, this.state.travelDateYear)}
                                </option>
                              ))}
                            </FormSelect>
                            <FormSelect
                              combineLabel
                              label="Day*"
                              id="travelDateDay"
                              name="travelDateDay"
                              className="flex-grow"
                              value={this.state.travelDateDay}
                              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                if (!!e.target.value)
                                  this.setState({
                                    travelDateDay: Number(e.target.value),
                                  });
                              }}
                            >
                              {dayOptions(this.state.travelDateYear, this.state.travelDateMonth).map((day) => (
                                <option key={`travel-day-${day}`} value={day}>
                                  {day}
                                </option>
                              ))}
                            </FormSelect>
                            <FormSelect
                              combineLabel
                              label="Year*"
                              id="travelDateYear"
                              name="travelDateYear"
                              className="flex-grow"
                              value={this.state.travelDateYear}
                              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                if (!!e.target.value)
                                  this.setState({
                                    travelDateYear: Number(e.target.value),
                                  });
                              }}
                            >
                              {YEARS.map((year) => (
                                <option key={`travel-year-${year}`} value={year}>
                                  {year}
                                </option>
                              ))}
                            </FormSelect>
                          </fieldset>
                        </div>
                      </>
                    ) : null}

                    <FormFile
                      label="Image Upload"
                      description="Upload size is limited to 2 MB. Supported formats: .jpg, .png, .bmp, .gif, .svg."
                      id="fileUpload"
                      name="images[]"
                      multiple
                      onChange={(e: ChangeEvent<HTMLInputElement>) => this.handleImageChange(e.target.files!)}
                    />

                    <button
                      type="submit"
                      onClick={(e: MouseEvent) => this.submit(e)}
                      className="tracker-button-email-submit-20200317-115201 btn-info btn"
                    >
                      Submit
                    </button>
                  </div>

                  {this.state.error && <div>There was a problem submitting this form. Please try again.</div>}
                </form>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-lighter py-1">
          <NextSteps
            ctaPageName="EmailUs"
            salesCallout1={<SalesCalloutHTO ctaPageName="EmailUs" />}
            salesCallout2={<SalesCalloutSwatches ctaPageName="EmailUs" />}
          />
        </div>
      </>
    );
  }
}

export const getStaticProps = withPageStaticProps();

export default EmailUs;
